import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Link } from "wouter";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { Code, Cloud, Sparkles, Zap, ArrowRight, ChevronDown, Rocket, Check } from "lucide-react";
import { useMediaQuery } from "@/hooks/useMediaQuery";

// Particle component for background
const Particle = ({ x, y, size, delay, duration }: { x: string; y: string; size: number; delay: number; duration: number }) => (
  <motion.div
    className="absolute rounded-full bg-gradient-to-r from-indigo-500/30 to-fuchsia-500/30"
    style={{
      width: `${size}px`,
      height: `${size}px`,
      x,
      y,
    }}
    initial={{ opacity: 0 }}
    animate={{
      opacity: [0, 0.6, 0],
      scale: [0, 1.2, 0],
    }}
    transition={{
      duration: duration,
      delay: delay,
      repeat: Infinity,
      repeatType: "loop",
      ease: "easeInOut",
    }}
  />
);

const FloatingDevices = () => {
  // Generate subtle animation for floating effect
  const floatingAnimation: {
    y: number[];
    transition: {
      duration: number;
      repeat: number;
      repeatType: 'reverse' | 'loop' | 'mirror';
      ease: string;
      delay?: number;
    };
  } = {
    y: [0, -15, 0],
    transition: {
      duration: 6,
      repeat: Infinity,
      repeatType: 'reverse',
      ease: 'easeInOut'
    }
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto mt-32 lg:mt-0 lg:absolute lg:right-0 lg:top-48 lg:translate-y-0">
      {/* Laptop */}
      <motion.div
        initial={{ y: 20, opacity: 0, rotateY: 15 }}
        animate={{ y: 0, opacity: 1, rotateY: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="relative z-10 w-full max-w-xl mx-auto"
      >
        <motion.div 
          className="relative"
          animate={floatingAnimation}
          transition={floatingAnimation.transition}
        >
          {/* Laptop Glow */}
          <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-3xl blur-xl -z-10" />
          
          {/* Laptop Screen */}
          <div className="relative bg-gray-900 border border-gray-700/50 rounded-xl overflow-hidden shadow-2xl shadow-blue-500/20">
            {/* Screen Bezel */}
            <div className="h-1 bg-gray-800 border-b border-gray-700/50 flex items-center justify-center">
              <div className="w-16 h-1 bg-gray-900 rounded-b-full" />
            </div>
            
            {/* Browser Chrome */}
            <div className="h-8 bg-gray-800 flex items-center px-3 border-b border-gray-700/50">
              <div className="flex space-x-2">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
              </div>
              <div className="flex-1 bg-gray-700/50 mx-3 h-5 rounded-md text-[10px] flex items-center px-3 text-gray-300 font-medium">
                zeexsoft.com/dashboard
              </div>
              <div className="flex space-x-1.5">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="w-1 h-1 rounded-full bg-gray-500/50" />
                ))}
              </div>
            </div>
            
            {/* App Content */}
            <div className="p-4 bg-gradient-to-br from-gray-900 to-gray-800 h-64">
              <div className="flex space-x-4 h-full">
                {/* Sidebar */}
                <div className="w-14 bg-gray-800/50 rounded-lg p-2 flex flex-col items-center space-y-3">
                  {['grid', 'users', 'settings', 'bell'].map((icon, i) => (
                    <div key={i} className="w-8 h-8 bg-gray-700/50 rounded-lg flex items-center justify-center text-gray-400">
                      <div className="text-xs">{icon === 'grid' ? '≡' : icon === 'users' ? '👥' : icon === 'settings' ? '⚙' : '🔔'}</div>
                    </div>
                  ))}
                </div>
                
                {/* Main Content */}
                <div className="flex-1 bg-gray-800/30 backdrop-blur-sm rounded-lg p-4 border border-gray-700/30">
                  <div className="text-white text-lg font-bold mb-2">We Architect</div>
                  <div className="text-blue-400 text-2xl font-bold mb-4">Tomorrow's Tech</div>
                  <div className="space-y-3">
                    {['Welcome back,', 'Your dashboard is ready!', '3 new notifications'].map((text, i) => (
                      <div key={i} className="h-2.5 w-full bg-gray-700/50 rounded-full animate-pulse" style={{
                        animationDelay: `${i * 0.2}s`,
                        width: i === 0 ? '60%' : i === 1 ? '80%' : '40%'
                      }} />
                    ))}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mt-6">
                    {['📊 Stats', '📈 Analytics', '📱 Users', '⚙ Settings'].map((item, i) => (
                      <div key={i} className="bg-gray-700/20 hover:bg-gray-700/40 transition-colors rounded-lg p-3 border border-gray-700/30 cursor-pointer">
                        <div className="text-xs text-gray-400 mb-1">{item.split(' ')[0]}</div>
                        <div className="text-xs font-medium text-white">{item.split(' ')[1]}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Laptop Bottom */}
          <div className="h-3 w-3/4 mx-auto bg-gradient-to-t from-gray-700 to-gray-600 rounded-b-lg shadow-lg" />
          <div className="h-1 w-1/3 mx-auto bg-gray-500/20 rounded-b-full" />
        </motion.div>
      </motion.div>
      
      {/* Phone */}
      <motion.div
        initial={{ x: -40, y: 40, opacity: 0, scale: 0.8 }}
        animate={{ x: 0, y: 0, opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="absolute top-20 left-6 z-20 w-36"
      >
        <motion.div 
          className="relative"
          animate={{
            y: floatingAnimation.y.map(y => y * 1.5), // Slightly more pronounced float
            transition: {
              ...floatingAnimation.transition,
              repeatType: 'reverse' as const,
              delay: 0.5 // Slight delay for more natural movement
            }
          }}
        >
          {/* Phone Glow */}
          <div className="absolute -inset-2 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-4xl blur-lg -z-10" />
          
          {/* Phone Frame */}
          <div className="relative bg-gray-900 border border-gray-700/50 rounded-[2.75rem] p-1.5 shadow-2xl shadow-purple-500/20 overflow-hidden">
            {/* Notch */}
            <div className="h-5 w-16 bg-black absolute top-0 left-1/2 -translate-x-1/2 rounded-b-2xl border-l border-r border-b border-gray-800/50 z-10" />
            
            {/* Screen */}
            <div className="relative bg-black rounded-[1.75rem] overflow-hidden h-72">
              {/* Status Bar */}
              <div className="h-6 px-3 bg-gradient-to-r from-blue-600 to-purple-600 flex justify-between items-center text-white">
                <div className="text-[8px] font-bold">9:41</div>
                <div className="flex items-center space-x-0.5">
                  <div className="w-1 h-1 rounded-full bg-white/80" />
                  <div className="w-1 h-1.5 rounded-sm bg-white/80" />
                  <div className="w-1 h-2 rounded-sm bg-white/80" />
                  <div className="w-0.5 h-2.5 rounded-full bg-white/80" />
                </div>
              </div>
              
              {/* App Header */}
              <div className="p-3 bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="h-3 w-24 bg-gray-700/80 rounded-full" />
                  <div className="w-6 h-6 rounded-full bg-blue-500/80" />
                </div>
              </div>
              
              {/* App Content */}
              <div className="p-3 space-y-2 bg-black">
                {/* Search Bar */}
                <div className="h-8 bg-gray-900/80 rounded-lg border border-gray-800 flex items-center px-3">
                  <div className="w-3 h-3 rounded-full border border-gray-500 mr-2" />
                  <div className="h-2 w-16 bg-gray-700/50 rounded-full" />
                </div>
                
                {/* Cards */}
                <div className="space-y-2">
                  {[0, 1].map((i) => (
                    <div key={i} className="bg-gray-900/80 rounded-xl p-3 border border-gray-800/50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="h-2.5 w-16 bg-gray-700/80 rounded-full" />
                        <div className="w-4 h-4 rounded-full bg-blue-500/80" />
                      </div>
                      <div className="h-1.5 w-24 bg-gray-800/50 rounded-full mb-2" />
                      <div className="h-1 w-16 bg-gray-800/30 rounded-full" />
                    </div>
                  ))}
                </div>
                
                {/* Bottom Navigation */}
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-gray-900/80 backdrop-blur-sm border-t border-gray-800/50 flex justify-around items-center px-4">
                  {['🏠', '🔍', '➕', '💬', '👤'].map((icon, i) => (
                    <div key={i} className={`text-lg ${i === 2 ? 'text-blue-400' : 'text-gray-400'}`}>
                      {icon}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Home Button */}
            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-16 h-1 rounded-full bg-gray-800/50" />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

const ScrollIndicator = () => (
  <motion.div 
    initial={{ opacity: 0, y: 10 }}
    animate={{ 
      opacity: [0, 1, 0],
      y: [10, 0, -10]
    }}
    transition={{ 
      duration: 2, 
      repeat: Infinity, 
      repeatType: "loop" 
    }}
    className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center text-muted-foreground/60"
  >
    <ChevronDown className="w-6 h-6 mb-1" />
    <span className="text-sm font-medium">Scroll to explore</span>
  </motion.div>
);

const AnimatedText = ({ text, className = "" }: { text: string; className?: string }) => {
  return (
    <span className="relative">
      {text.split('').map((char, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.5,
            delay: i * 0.03,
          }}
          className={`inline-block ${className}`}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
    </span>
  );
};

function HeroSection() {
  const { openDrawer } = useQuoteDrawer();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.05]);

  const scrollToServices = () => {
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Generate random particles
  const particles = Array.from({ length: 15 }).map((_, i) => ({
    id: i,
    x: `${Math.random() * 100}%`,
    y: `${Math.random() * 100}%`,
    size: Math.random() * 10 + 5,
    delay: Math.random() * 5,
    duration: Math.random() * 10 + 10
  }));

  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    const handleScroll = () => {
      // Your scroll logic here
    };
    
    // Use passive event listener for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const stats = [
    { value: '100+', label: 'Projects', icon: '🚀', color: 'from-blue-500 to-cyan-400' },
    { value: '50+', label: 'Clients', icon: '😊', color: 'from-purple-500 to-blue-500' },
    { value: '98%', label: 'Satisfaction', icon: '⭐', color: 'from-amber-500 to-orange-400' },
    { value: '24/7', label: 'Support', icon: '🛡️', color: 'from-emerald-500 to-cyan-400' },
  ];

  return (
    <section 
      ref={containerRef} 
      className="relative min-h-screen w-full flex items-center justify-center overflow-x-hidden bg-gradient-to-br from-gray-900 via-indigo-900 to-fuchsia-900"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden -z-10">
        <motion.div 
          style={{ y, scale }}
          className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent"
        />
        
        {/* Floating shapes */}
        <motion.div
          animate={{ 
            y: [0, -30, 0],
            rotate: [0, 8, 0]
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-3xl"
        />
        
        <motion.div
          animate={{ 
            y: [0, 30, 0],
            rotate: [0, -8, 0]
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity, 
            ease: "easeInOut",
            delay: 5
          }}
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-tr from-secondary/10 to-transparent rounded-full blur-3xl"
        />
        
        {/* Particles */}
        <div className="absolute inset-0">
          {particles.map((particle) => (
            <Particle key={particle.id} {...particle} />
          ))}
        </div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-grid-pattern opacity-10">
          {/* Grid content would go here */}
        </div>
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/40 to-background/80">
          {/* Gradient content would go here */}
        </div>
      </div>

      <div className="w-full max-w-[100vw] px-4 sm:px-6 relative z-10 py-20 md:py-24 lg:py-32">
        <div className="w-full max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: isMobile ? 0.3 : 0.6 }}
                className="inline-flex items-center justify-center mb-6"
              >
                <motion.span 
                  className="h-2 w-2 bg-indigo-400 rounded-full mr-2"
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.8, 1, 0.8]
                  }}
                  transition={{ 
                    duration: 2, 
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                <span className="text-sm font-medium text-indigo-300 flex items-center">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5 text-yellow-300" />
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 to-fuchsia-400">
                    Innovating Since 2017
                  </span>
                  <ChevronDown className="w-4 h-4 ml-1.5 text-indigo-300 group-hover:translate-y-0.5 transition-transform" />
                </span>
              </motion.div>

              {/* Main Heading */}
              <div className="text-center space-y-4">
                
                <motion.h1
                  className="text-[2.75rem] md:text-5xl font-bold leading-tight"
                >
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-600">
                    We Architect
                  </span>
                  <br />
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-blue-600">
                    Tomorrow's Tech
                  </span>
                </motion.h1>
              </div>

              {/* Subheading */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: isMobile ? 0.3 : 0.6, delay: 0.3 }}
                className="mb-12 max-w-2xl mx-auto lg:mx-0"
              >
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed mb-3">
                  Transforming visionary ideas into cutting-edge digital solutions. Based in Sharjah, UAE, we specialize in crafting exceptional web applications, mobile experiences, and enterprise software.
                </p>
                <div className="space-y-1.5 mb-4">
                  {[
                    { text: "Custom Web & Mobile Development", icon: Code },
                    { text: "UI/UX Design & Branding", icon: Sparkles },
                    { text: "Cloud & DevOps Solutions", icon: Cloud },
                    { text: "AI & Machine Learning", icon: Zap }
                  ].map((item, index) => (
                    <motion.div 
                      key={item.text}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 + (index * 0.1) }}
                      className="flex items-center text-muted-foreground/80 text-[11px] md:text-xs"
                    >
                      <Check className="w-3 h-3 text-blue-500 mr-1.5" />
                      <span>{item.text}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: isMobile ? 0.3 : 0.6, delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4 justify-start items-start sm:items-center"
              >
                <Button 
                  onClick={scrollToServices}
                  size="lg"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  className="relative overflow-hidden group px-8 py-6 text-lg font-semibold rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-500/20"
                >
                  <Link href="/portfolio" className="relative z-10 flex items-center">
                    Explore Our Work
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <span className="absolute inset-0 bg-gradient-to-r from-blue-400/0 via-white/10 to-purple-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></span>
                  {isHovered && (
                    <motion.span 
                      className="absolute inset-0 bg-white/5"
                      initial={{ x: '-100%', opacity: 0 }}
                      animate={{ x: '100%', opacity: 0.3 }}
                      transition={{ duration: 1.5, ease: 'easeInOut' }}
                    />
                  )}
                </Button>
                
                <Button 
                  onClick={openDrawer}
                  variant="outline"
                  size="lg"
                  className="group px-8 py-6 text-lg font-semibold rounded-xl border border-border/50 hover:border-blue-400/50 bg-background/70 backdrop-blur-sm hover:bg-blue-500/5 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg"
                >
                  <span className="relative z-10 flex items-center">
                    Get Free Quote
                    <Rocket className="ml-2 w-5 h-5 text-blue-500 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Button>
              </motion.div>
              
              {/* Trust indicators */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-8 pt-6 border-t border-border/20"
              >
                <p className="text-xs text-muted-foreground text-center mb-4">Trusted by industry leaders</p>
                <div className="flex flex-wrap justify-center gap-3 px-4">
                  {[
                    { name: 'Microsoft', bg: 'bg-blue-500', text: 'text-white' },
                    { name: 'Google', bg: 'bg-white', text: 'text-gray-800' },
                    { name: 'Amazon', bg: 'bg-yellow-400', text: 'text-gray-900' },
                    { name: 'Uber', bg: 'bg-black', text: 'text-white' },
                    { name: 'Netflix', bg: 'bg-red-600', text: 'text-white' },
                  ].map((brand) => (
                    <div 
                      key={brand.name}
                      className={`${brand.bg} ${brand.text} text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap`}
                    >
                      {brand.name}
                    </div>
                  ))}
                </div>
              </motion.div>

            </div>
            
            {/* Floating Devices */}
            <FloatingDevices />
          </div>
          
          {/* Scroll Indicator */}
          <ScrollIndicator />

        </div>
      </div>
    </section>
  );
};

export default HeroSection;
